import { filter, find, map, size } from 'lodash/fp';
import { DateTime } from 'luxon';

import { ViewMode } from '../components/common/PointingDetail';
import { Mad, OdooEmployee, OdooMad, OdooPointing, OdooWorkPlace, Pointing } from '../types/models';
import { AppConfig } from './Global';

export interface ConstraintContext {
    appConfig?: AppConfig;
    mads?: Mad[];
    workPlaces?: OdooWorkPlace[];
    employees?: OdooEmployee[];
    currentDate?: string;
}

export interface AutoAssignmentResult {
    employee_id?: number;
    mad_id?: number;
    workplace_id?: number;
    city?: string;
    warnings?: string[];
}

export interface ValidationResult {
    isValid: boolean;
    errorMessage?: string;
}

export interface VisibilityRules {
    showOtherMad: boolean;
    showOtherWorkplace: boolean;
    showCity: boolean;
    showCompanySelector: boolean;
}

/**
 * Auto-assignment constraints and business logic
 */
export class PointingConstraints {
    private context: ConstraintContext;

    constructor(context: ConstraintContext) {
        this.context = context;
    }

    /**
     * Apply all auto-assignment rules to a pointing
     */
    applyAutoAssignmentRules(pointing: Partial<Pointing>, viewMode?: ViewMode): AutoAssignmentResult {
        const result: AutoAssignmentResult = {
            warnings: [],
        };

        // Auto assign employee if there is only one
        if (this.context.employees && size(this.context.employees) === 1) {
            result.employee_id = this.context.employees[0].id;
        }

        // Get mads linked to the employee
        const activeMadsOfEmployee = this.getActiveMadsForEmployee(
            pointing.employee_id || result.employee_id || 0,
            pointing.date || this.context.currentDate || DateTime.now().toISODate(),
        );

        // Handle multiple MADs for Adherent view
        if (viewMode === ViewMode.Adherent && size(activeMadsOfEmployee) > 1) {
            result.warnings?.push('Plusieurs MAD sont associées à cet employé');
            result.mad_id = 0;
        } else if (size(activeMadsOfEmployee) === 1 && pointing.mad_id !== 0) {
            // Auto assign mad if there is only one
            result.mad_id = activeMadsOfEmployee[0].id;
        } else if (
            size(activeMadsOfEmployee) === 0 &&
            (pointing.employee_id || result.employee_id) &&
            (pointing.employee_id || result.employee_id)! >= 0
        ) {
            result.mad_id = 0;
        }

        // Get workplaces linked to the mad
        const madId = result.mad_id ?? pointing.mad_id ?? 0;
        const workPlacesOfMad = this.getWorkplacesForMad(madId);

        // Auto assign workplace if there is only one
        if (size(workPlacesOfMad) === 1 && pointing.workplace_id !== 0) {
            result.workplace_id = workPlacesOfMad[0].id;
        }

        // If no workplace available, set workplace_id to 0
        if (madId >= 0 && size(workPlacesOfMad) === 0) {
            result.workplace_id = 0;
        }

        // Handle city assignment
        if (pointing.workplace_id !== undefined || result.workplace_id !== undefined) {
            const workplaceId = result.workplace_id ?? pointing.workplace_id ?? 0;

            if (workplaceId <= 0) {
                result.city = '';
            } else if (workplaceId > 0) {
                const workplace = workPlacesOfMad.find((w) => w.id === workplaceId);
                result.city = workplace?.city || '';
            }
        }

        return result;
    }

    /**
     * Get active MADs for an employee on a specific date
     */
    private getActiveMadsForEmployee(employeeId: number, date: string): Mad[] {
        if (!this.context.mads || !employeeId) return [];

        return filter(
            (m) => m.employee_id[0] === employeeId && !(m as any).disabled, // TODO: fix this
            this.adjustMadsPropertiesByTime(this.context.mads, DateTime.fromISO(date)),
        );
    }

    /**
     * Get workplaces for a specific MAD
     */
    private getWorkplacesForMad(madId: number): OdooWorkPlace[] {
        if (!this.context.workPlaces) return [];
        return filter((w) => w.mad_ids.includes(madId), this.context.workPlaces);
    }

    /**
     * Adjust MAD properties by time - marks MADs as disabled based on date ranges
     */
    private adjustMadsPropertiesByTime(mads: Mad[], date: DateTime): Mad[] {
        return map((mad: Mad) => {
            const mutatedMad = {
                ...mad,
                disabled: false,
            };

            if (mad.date_end && date > mad.date_end) {
                mutatedMad.display_name = `(Terminé) ${mutatedMad.display_name}`;
                mutatedMad.disabled = true;
            } else if (mad.date_start && date < mad.date_start) {
                mutatedMad.display_name = `(Futur) ${mutatedMad.display_name}`;
                mutatedMad.disabled = true;
            }

            return mutatedMad;
        }, mads);
    }
}

/**
 * Lunch validation constraints
 */
export class LunchConstraints {
    /**
     * Validate lunch constraint (total lunch + lunch_night <= 1)
     */
    static validateLunchConstraint(
        appConfig: AppConfig | undefined,
        field: string,
        newValue: number,
        currentLunch: number,
        currentLunchNight: number,
    ): ValidationResult {
        if (!appConfig?.limitLunchToOne || (field !== 'lunch' && field !== 'lunch_night')) {
            return { isValid: true };
        }

        const totalLunch =
            field === 'lunch' ? (newValue || 0) + (currentLunchNight || 0) : (currentLunch || 0) + (newValue || 0);

        if (totalLunch > 1) {
            return {
                isValid: false,
                errorMessage: 'Le nombre total de paniers (jour + nuit) ne peut pas dépasser 1 pour une même journée.',
            };
        }

        return { isValid: true };
    }

    /**
     * Get maximum allowed lunch value based on constraints
     */
    static getMaxLunchValue(appConfig: AppConfig | undefined): number | undefined {
        return appConfig?.limitLunchToOne ? 1 : undefined;
    }
}

/**
 * Workplace visibility and validation constraints
 */
export class WorkplaceConstraints {
    /**
     * Determine visibility rules for workplace-related fields
     */
    static getVisibilityRules(pointing: Partial<Pointing>, employees?: OdooEmployee[]): VisibilityRules {
        return {
            showOtherMad: pointing.mad_id === 0,
            showOtherWorkplace: pointing.workplace_id === 0,
            showCity: (pointing.workplace_id ?? 0) > 0,
            showCompanySelector: pointing.mad_id === 0 && size(employees) > 1,
        };
    }

    /**
     * Prepare workplace data for Odoo update
     */
    static prepareWorkplaceUpdateData(workplaceValue: {
        workplace_id: [number, string] | false;
        other_workplace: string;
        city?: string;
    }): {
        workplace_id: number | false;
        other_workplace: string;
        city?: string;
    } {
        let workplaceId: number | false;

        if (workplaceValue.workplace_id === false) {
            workplaceId = false;
        } else if (Array.isArray(workplaceValue.workplace_id)) {
            workplaceId = workplaceValue.workplace_id[0]; // Extract only the ID
        } else {
            workplaceId = workplaceValue.workplace_id as number;
        }

        return {
            workplace_id: workplaceId,
            other_workplace: workplaceValue.other_workplace || '',
            ...(workplaceValue.city !== undefined && { city: workplaceValue.city }),
        };
    }

    /**
     * Get available workplaces for a MAD
     */
    static getAvailableWorkplaces(madId: number, workPlaces: OdooWorkPlace[]): OdooWorkPlace[] {
        return filter((w) => w.mad_ids.includes(madId) || w.id === 0, workPlaces);
    }
}

/**
 * Field visibility constraints based on company attributes
 */
export class FieldVisibilityConstraints {
    static readonly COMPANY_ATTRIBUTE_MAPPING = {
        working_hours: ['pointing_rubric_working_hours_id'],
        working_night_hours: ['pointing_rubric_working_night_hours_ids'],
        bad_weather_hours: ['pointing_rubric_bad_weather_hours_id'],
        absence_hours: ['pointing_rubric_absence_hours_id'],
        lunch: ['pointing_rubric_lunch_id'],
        lunch_night: ['pointing_rubric_lunch_night_id'],
        travel: ['pointing_rubric_travel_ids'],
    } as const;

    /**
     * Check if a field should be visible based on company attributes
     */
    static isFieldVisible(
        fieldName: keyof typeof FieldVisibilityConstraints.COMPANY_ATTRIBUTE_MAPPING,
        companyAttributes: string[],
    ): boolean {
        const requiredAttributes = FieldVisibilityConstraints.COMPANY_ATTRIBUTE_MAPPING[fieldName];
        return requiredAttributes.some((attr) => companyAttributes.includes(attr));
    }
}

/**
 * View mode constraints
 */
export class ViewModeConstraints {
    /**
     * Determine field editability based on view mode and conditions
     */
    static isFieldEditable(viewMode: ViewMode, fieldName: string, pointingId?: number): boolean {
        switch (fieldName) {
            case 'employee_note':
                return viewMode !== ViewMode.Adherent;
            case 'adherent_note':
                return viewMode === ViewMode.Adherent;
            case 'real_time_start':
            case 'real_time_end':
                return viewMode !== ViewMode.Confirm;
            default:
                return true;
        }
    }

    /**
     * Determine field visibility based on view mode and conditions
     */
    static isFieldVisible(viewMode: ViewMode, fieldName: string, pointingId?: number): boolean {
        switch (fieldName) {
            case 'employee_selector':
                return viewMode === ViewMode.Adherent;
            case 'mad_selector':
                return viewMode !== ViewMode.Adherent;
            case 'real_time_range':
                return viewMode === ViewMode.Confirm;
            case 'employee_note':
                return (
                    (pointingId !== undefined && pointingId > 0 && viewMode === ViewMode.Adherent) ||
                    viewMode !== ViewMode.Adherent
                );
            case 'adherent_note':
                return viewMode === ViewMode.Adherent;
            default:
                return true;
        }
    }
}

/**
 * Combined constraints validator
 */
export class PointingValidator {
    private constraints: PointingConstraints;
    private context: ConstraintContext;

    constructor(context: ConstraintContext) {
        this.context = context;
        this.constraints = new PointingConstraints(context);
    }

    /**
     * Validate a complete pointing update
     */
    validatePointingUpdate(
        originalPointing: Partial<Pointing>,
        updates: Partial<Pointing>,
        viewMode?: ViewMode,
    ): ValidationResult & { autoAssignments?: AutoAssignmentResult } {
        const mergedPointing = { ...originalPointing, ...updates };

        // Apply auto-assignment rules
        const autoAssignments = this.constraints.applyAutoAssignmentRules(mergedPointing, viewMode);

        // Validate lunch constraints
        if (updates.lunch !== undefined || updates.lunch_night !== undefined) {
            const lunchValidation = LunchConstraints.validateLunchConstraint(
                this.context.appConfig,
                updates.lunch !== undefined ? 'lunch' : 'lunch_night',
                updates.lunch ?? updates.lunch_night ?? 0,
                mergedPointing.lunch ?? 0,
                mergedPointing.lunch_night ?? 0,
            );

            if (!lunchValidation.isValid) {
                return lunchValidation;
            }
        }

        return {
            isValid: true,
            autoAssignments,
        };
    }

    /**
     * Get visibility rules for the current pointing
     */
    getVisibilityRules(pointing: Partial<Pointing>): VisibilityRules {
        return WorkplaceConstraints.getVisibilityRules(pointing, this.context.employees);
    }
}
